"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { TInterview, TUserAvailability } from "@/actions/types";
import { SupportedLanguage } from "@/lib/shared";
import CustomFormWithValidations from "@/components/customs/CustomFormWithValidations";
import z from "zod";
import { toast } from "sonner";
import { requestInterview, updateInterviewStatus } from "@/actions/interviews";
import { TimeSlotSelector } from "../Settings";
import { DatePicker } from "@/components/ui/date-picker";
import { format } from "date-fns";
import getTimezones from "@/lib/getTimezones";
import { getProfileAvailabilityById } from "@/actions/profiles";
import { Loader2 } from "lucide-react";
import { getToken } from "@/actions/auth";
import { useRouter } from "next/navigation";
import { localizedPath } from "@/lib/localePath";

type RequestMeetingProps = {
  triggerClassName?: string;
  item?: TInterview;
  participantId: string;
  buttonText: React.ReactNode;
  dialogTitle: string;
  nodeButton?: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
  isOpenDialog?: boolean;
  locale: SupportedLanguage;
};

const requestMeetingSchema = z.object({
  date: z.string().min(1, "Date is required"),
  time: z.string().min(1, "Time is required"),
  timeZone: z.string().min(1, "Time zone is required"),
});

export default function RequestMeeting({
  triggerClassName,
  item,
  participantId,
  buttonText,
  dialogTitle,
  nodeButton,
  onOpenChange,
  isOpenDialog,
  locale,
}: RequestMeetingProps) {
  const router = useRouter();
  const [isOpen, isOpenSet] = useState(!!isOpenDialog);
  const [isFetchingAvailability, isFetchingAvailabilitySet] = useState(false);

  const [userAvailability, userAvailabilitySet] = useState<TUserAvailability[]>(
    []
  );

  const [isLoading, isLoadingSet] = useState(false);

  const handleSubmit = async (
    formData: z.infer<typeof requestMeetingSchema>
  ) => {
    isLoadingSet(true);

    try {
      const parsedData = requestMeetingSchema.parse(formData);

      // Validate that the selected date is not in the past
      const selectedDate = new Date(parsedData.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison

      if (selectedDate < today) {
        throw new Error("Selected date cannot be in the past");
      }

      if (item && ["Upcoming", "Pending"].includes(item.status)) {
        // Cancel the existing interview only happen when reschedule purpose
        await updateInterviewStatus(item.id, {
          appointmentAction: "Cancel",
          reason: `Rescheduled to ${format(new Date(parsedData.date), "PPpp")}`,
        });
      }

      const res = await requestInterview(participantId, {
        date: format(new Date(parsedData.date), "yyyy-MM-dd"),
        time: format(new Date(`2025-09-03T${parsedData.time}`), "HH:mm"),
        timeZone: parsedData.timeZone,
      });

      if (res.status === "error") {
        throw new Error(res.message);
      }

      toast.success(res.message || "Interview requested successfully");

      isLoadingSet(false);
      handleOpenChange(false);
    } catch (error) {
      console.log({ error });

      isLoadingSet(false);
      toast.error(error instanceof Error ? error.message : "Reschedule failed");
    }
  };

  async function onChangeDate(date: Date | null, timeZone?: string) {
    if (date) {
      try {
        isFetchingAvailabilitySet(true);
        const res = await getProfileAvailabilityById(
          participantId,
          format(date, "yyyy-MM-dd"),
          timeZone
        );

        if (res.status === "success") {
          const availableSlots = res.metaData.filter(
            (a) => a.status === "Available"
          );
          userAvailabilitySet(availableSlots);
        }
        isFetchingAvailabilitySet(false);
      } catch (error) {
        console.log({ error });
        isFetchingAvailabilitySet(false);
      }
    }
  }

  const handleOpenChange = async (open: boolean) => {
    const token = await getToken();
    console.log({ token });

    if (!token) {
      const returnTo = localizedPath(
        locale,
        `/profiles/${participantId}?action=request-meeting`,
      );
      router.push(
        localizedPath(
          locale,
          `/auth/login?redirect=${encodeURIComponent(returnTo)}`,
        ),
      );
      return;
    }

    isOpenSet(open);
    onOpenChange?.(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {nodeButton || (
          <Button variant="outline" rounded="full" className={triggerClassName}>
            {buttonText}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-lg max-h-[calc(100vh-10rem)] !pb-0"
        showCloseButton={false}
      >
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-semibold">
            {dialogTitle}
          </DialogTitle>
        </DialogHeader>

        <CustomFormWithValidations<z.infer<typeof requestMeetingSchema>>
          schema={requestMeetingSchema}
          onSubmit={handleSubmit}
          defaultValues={{
            date: "",
            time: "",
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }}
        >
          {(form) => {
            return (
              <>
                <div className="space-y-6 overflow-y-auto max-h-[calc(100vh-20rem)]">
                  {item && (
                    <div className="border rounded-lg p-4 bg-white relative">
                      <div className="flex items-center mb-4">
                        <div className="bg-black text-white rounded-lg p-3 flex flex-col items-center justify-center mr-3">
                          <span className="text-xl font-bold">
                            {format(item.appointmentTime, "d")}
                          </span>
                          <span className="text-xs">
                            {format(item.appointmentTime, "MMM")}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-semibold">
                            {item.participantName}
                          </h3>
                          <p className="text-gray-500 text-sm">
                            {item.participantOccupation || "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="">Time</span>
                        <span className="">
                          {format(item.appointmentTime, "h:mm a")}
                        </span>
                      </div>
                    </div>
                  )}

                  <FormField
                    control={form.control}
                    name="timeZone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Time Zone</FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select your timezone" />
                            </SelectTrigger>
                            <SelectContent className="max-h-56">
                              {getTimezones().map((timezone) => (
                                <SelectItem key={timezone} value={timezone}>
                                  {timezone.replace(/_/g, " ")}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Start Date</FormLabel>
                        <FormControl>
                          <DatePicker
                            modal
                            date={
                              field.value ? new Date(field.value) : undefined
                            }
                            onSelect={(date) => {
                              if (date) {
                                // Check if selected date is lower than today
                                const selectedDate = new Date(date);
                                const today = new Date();
                                today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
                                selectedDate.setHours(0, 0, 0, 0);

                                if (selectedDate < today) {
                                  toast.error(
                                    "Selected date cannot be in the past"
                                  );
                                  return;
                                }
                              }

                              field.onChange(date?.toISOString() || "");
                              onChangeDate(
                                date ? new Date(date) : null,
                                form.watch("timeZone")
                              );
                            }}
                            placeholder="dd/mm/yyyy"
                            className="w-full"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* {form.watch("date") && <></>} */}
                  <div className="space-y-2">
                    <FormLabel className="relative">
                      Time Slots{" "}
                      {!form.watch("date") && (
                        <span className="text-red-500">
                          (Select Date First)
                        </span>
                      )}
                      {isFetchingAvailability && (
                        <span className="absolute top-0 right-1 bottom-0 flex items-center justify-center aspect-square">
                          <Loader2 size={16} className="animate-spin" />
                        </span>
                      )}
                    </FormLabel>
                    <div className="border rounded-lg p-4">
                      <TimeSlotSelector
                        availableSlots={userAvailability.map((a) => a.hour)}
                        selectedSlots={form.watch("time") || ""}
                        onSlotsChange={(slot) => {
                          form.setValue("time", slot);
                        }}
                        locale={locale}
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="sticky bottom-0 bg-white flex justify-between items-center py-4">
                    <Button
                      rounded="full"
                      type="button"
                      variant="outline"
                      className="px-8"
                      disabled={isLoading}
                      onClick={() => handleOpenChange(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      rounded="full"
                      type="submit"
                      className="px-8 bg-black hover:bg-gray-800"
                      disabled={isLoading}
                    >
                      {isLoading ? "Requesting..." : "Request"}
                    </Button>
                  </div>
                </div>
              </>
            );
          }}
        </CustomFormWithValidations>
      </DialogContent>
    </Dialog>
  );
}
